<template>
  <div>
    <div class="vx-card">
      <ConfigParamsHeader
        :application="application"
        :selected-user-id="selectedUserId"
        :is-super-admin="isSuperAdmin"
        @update:application="onApplicationChange"
        @update:selectedUserId="onUserChange"
      />

      <div class="vx-card__collapsible-content vs-con-loading__container">
        <div class="vx-card__body">
          <div
            v-if="error"
            class="alert alert-danger mb-3"
          >
            {{ error }}
          </div>

          <div class="config-params-panels">
            <div class="config-params-panels__tree">
              <ConfigParamsTree
                :tree-data="treeData"
                :is-loading="isLoadTree"
                :selected-id="selectedNodeId"
                :is-super-admin="isSuperAdmin"
                @select-item="onSelectItem"
                @context-action="onContextAction"
              />
            </div>

            <div class="config-params-panels__content">
              <div
                v-if="isLoadingConfigurations"
                class="demo-inline-spacing"
              >
                <b-spinner
                  key="primary"
                  class="mr-1"
                  variant="primary"
                />
              </div>

              <ConfigList
                v-if="selected && selected.name && !isLoadingConfigurations"
                :key="selected.optionId"
                :selected-user-id="selectedUserId"
                :config="selected"
                :application="application"
                :configurations="configurations"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfigParamsContextMenu
      ref="contextMenu"
      @action="onTreeAction"
    />

    <FolderModal
      :visible="showFolderModal"
      :folder="modalFolder"
      :parent-id="modalParentId"
      :children-count="modalChildrenCount"
      @save="onSaveComplete"
      @close="showFolderModal = false"
    />

    <ParameterModal
      :visible="showParameterModal"
      :parameter="modalParameter"
      :parent-id="modalParentId"
      :children-count="modalChildrenCount"
      :is-copy="modalIsCopy"
      @save="onSaveComplete"
      @close="showParameterModal = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { BSpinner } from 'bootstrap-vue';
import ConfigParamsHeader from '@/febos/global/empresas/componentes/configs/ConfigParamsHeader.vue';
import ConfigParamsTree from '@/febos/global/empresas/componentes/configs/ConfigParamsTree.vue';
import ConfigParamsContextMenu from '@/febos/global/empresas/componentes/configs/ConfigParamsContextMenu.vue';
import FolderModal from '@/febos/global/empresas/componentes/configs/FolderModal.vue';
import ParameterModal from '@/febos/global/empresas/componentes/configs/ParameterModal.vue';
import ConfigList from '@/febos/global/empresas/componentes/configs/ConfigList';
import { loadTree } from '@/febos/global/empresas/servicios/configParams.service';

export default {
  name: 'Configuraciones',
  components: {
    BSpinner,
    ConfigParamsHeader,
    ConfigParamsTree,
    ConfigParamsContextMenu,
    FolderModal,
    ParameterModal,
    ConfigList,
  },
  data() {
    return {
      application: process.env.VUE_APP_APP || 'ED',
      selectedUserId: '',
      treeData: [],
      selected: null,
      isLoadTree: false,
      error: null,
      showFolderModal: false,
      showParameterModal: false,
      modalFolder: null,
      modalParameter: null,
      modalParentId: null,
      modalChildrenCount: 0,
      modalIsCopy: false,
    };
  },
  computed: {
    ...mapGetters('Empresas', ['configurations', 'isLoadingConfigurations']),
    ...mapGetters('Usuario', ['hasPermission']),
    isSuperAdmin() {
      return this.hasPermission('FEB99');
    },
    selectedNodeId() {
      if (!this.selected) return null;
      return this.selected.optionId || this.selected.id || null;
    },
  },
  watch: {
    selectedUserId() {
      this.onContextChange();
    },
    application() {
      this.onContextChange();
    },
  },
  mounted() {
    this.loadTree();
  },
  methods: {
    ...mapActions('Empresas', ['loadConfigurations']),

    async loadTree() {
      this.isLoadTree = true;
      this.error = null;
      this.loadConfigurations({
        usuarioId: this.selectedUserId || '',
        app: this.application,
      });
      try {
        this.treeData = await loadTree();
      } catch (err) {
        this.error = err.message || 'Error al cargar el árbol de parámetros';
        this.treeData = [];
      } finally {
        this.isLoadTree = false;
      }
    },

    onContextChange() {
      this.selected = null;
      this.loadTree();
    },

    onApplicationChange(value) {
      this.application = value;
    },

    onUserChange(value) {
      this.selectedUserId = value;
    },

    onSelectItem(node) {
      this.selected = node;
    },

    onContextAction({ event, node }) {
      this.$refs.contextMenu.open(event, node);
    },

    onTreeAction({ action, node }) {
      switch (action) {
        case 'edit-folder':
          this.openFolderModal(node);
          break;
        case 'add-folder':
          this.openFolderModalForCreate(node);
          break;
        case 'add-parameter':
          this.openParameterModalForCreate(node);
          break;
        case 'edit-parameter':
          this.openParameterModal(node, false);
          break;
        case 'copy-parameter':
          this.openParameterModal(node, true);
          break;
        default:
          break;
      }
    },

    openFolderModal(folder) {
      this.modalFolder = folder;
      this.modalParentId = null;
      this.modalChildrenCount = 0;
      this.showFolderModal = true;
    },

    openFolderModalForCreate(parentNode) {
      this.modalFolder = null;
      this.modalParentId = parentNode.optionId || parentNode.id;
      this.modalChildrenCount = (parentNode.children || []).length;
      this.showFolderModal = true;
    },

    openParameterModalForCreate(parentNode) {
      this.modalParameter = null;
      this.modalParentId = parentNode.optionId || parentNode.id;
      this.modalChildrenCount = (parentNode.children || []).length;
      this.modalIsCopy = false;
      this.showParameterModal = true;
    },

    openParameterModal(parameter, isCopy) {
      this.modalParameter = parameter;
      this.modalParentId = parameter.padre || null;
      this.modalChildrenCount = 0;
      this.modalIsCopy = isCopy;
      this.showParameterModal = true;
    },

    onSaveComplete() {
      this.showFolderModal = false;
      this.showParameterModal = false;
      this.loadTree();
    },
  },
};
</script>

<style scoped>
.config-params-panels {
  display: flex;
  gap: 1rem;
  height: calc(100vh - 200px);
}

.config-params-panels__tree {
  flex: 0 0 33.33%;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  padding-right: 1rem;
}

.config-params-panels__content {
  flex: 1;
  overflow-y: auto;
  padding-left: 0.5rem;
}
</style>
